/**
 * GA4 module singleton — a thin convenience over `createGoogleAnalyticsProvider`
 * for the common SPA case: initialize once at startup, then record a page_view
 * on each client-side route change.
 *
 * Caller owns the measurement-ID policy (e.g. "prod default, dev off"):
 *
 * ```ts
 * import { initGA4, trackPageview } from '@gjl/analytics';
 *
 * const GA_ID =
 *   (import.meta.env.VITE_GA_ID as string | undefined) ||
 *   (import.meta.env.PROD ? 'G-XXXXXXXXXX' : undefined);
 *
 * initGA4(GA_ID);                 // no-op when GA_ID is falsy
 * // in a router effect:
 * trackPageview(location.pathname + location.search);
 * ```
 */

import { createGoogleAnalyticsProvider } from './providers/google-analytics'
import type { AnalyticsProvider } from './types'

let provider: AnalyticsProvider | null = null

/**
 * Initialize GA4 as a module singleton. No-op when `measurementId` is falsy
 * (so dev/un-provisioned builds stay clean) or off the browser. Idempotent.
 */
export function initGA4(measurementId: string | undefined | null): void {
  if (provider || !measurementId || typeof window === 'undefined') return
  provider = createGoogleAnalyticsProvider({ measurementId })
}

/**
 * Record a SPA page view for the given path. No-op until `initGA4` has run with
 * a valid measurement ID.
 */
export function trackPageview(path: string): void {
  if (!provider || typeof document === 'undefined') return
  provider.page(document.title, { path })
}

/** True once `initGA4` has been called with a valid measurement ID. */
export function analyticsEnabled(): boolean {
  return provider !== null
}
